import * as React from 'react';
import { Text } from '../../display';
import { InputMessage } from '../shared';

/**
 * Size variants for the TextField
 * - sm: 32px (compact)
 * - md: 40px (default)
 * - lg: 48px (comfortable)
 */
export type TextFieldSize = 'sm' | 'md' | 'lg';

/**
 * TextField component props
 * Extends native input attributes, omitting 'size' to avoid conflict with our size prop
 */
export interface TextFieldProps
  extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'size'> {
  /**
   * Label text displayed above the input
   */
  label?: string;
  /**
   * Helper text displayed below the input
   */
  helperText?: string;
  /**
   * Whether the input is in an error state
   */
  error?: boolean;
  /**
   * Error message displayed when error is true
   * Replaces helperText when present
   */
  errorMessage?: string;
  /**
   * Whether the field is required
   * Shows a red asterisk next to the label
   */
  required?: boolean;
  /**
   * Size variant of the input
   * @default 'md'
   */
  size?: TextFieldSize;
  /**
   * Element to display on the left side of the input (icon or text prefix)
   */
  leftElement?: React.ReactNode;
  /**
   * Element to display on the right side of the input (icon or text suffix)
   */
  rightElement?: React.ReactNode;
}

/**
 * Height classes for each size variant
 */
const sizeHeightClasses: Record<TextFieldSize, string> = {
  sm: 'h-8', // 32px
  md: 'h-10', // 40px
  lg: 'h-12', // 48px
};

/**
 * Builds the state-dependent CSS classes for the input element.
 * Extracted to reduce cognitive complexity of the main render function.
 */
function getStateClasses(error: boolean, disabled: boolean): string {
  if (disabled) {
    return [
      'bg-secondary-100',
      'text-secondary-400',
      'border-secondary-300',
      'cursor-not-allowed',
    ].join(' ');
  }

  if (error) {
    return [
      'border-error-700',
      'focus:ring-2',
      'focus:ring-error-700/20',
    ].join(' ');
  }

  return [
    'border-secondary-300',
    'focus:border-primary-400',
    'focus:ring-2',
    'focus:ring-primary-200',
  ].join(' ');
}

/**
 * Builds the complete className string for the input element.
 * Extracted to reduce cognitive complexity of the main render function.
 */
function buildInputClasses(
  size: TextFieldSize,
  error: boolean,
  disabled: boolean,
  leftElement: React.ReactNode,
  rightElement: React.ReactNode,
  className: string
): string {
  return [
    // Size
    sizeHeightClasses[size],
    // Base styling
    'w-full',
    'rounded-[6px]',
    'border',
    'px-3',
    'text-[15px]',
    'leading-[22px]',
    'outline-none',
    'text-secondary-800',
    'transition-all',
    'duration-150',
    'ease-out',
    // Placeholder
    'placeholder:text-secondary-400',
    // State-dependent classes
    getStateClasses(error, disabled),
    // Padding adjustments for left/right elements
    leftElement && 'pl-10',
    rightElement && 'pr-10',
    // Additional classes
    className,
  ]
    .filter(Boolean)
    .join(' ');
}

/**
 * TextField component for text input with label, helper text, and error states.
 *
 * Built following the design system specifications:
 * - Height: 40px (default), 32px (compact), 48px (comfortable)
 * - Border: 1px neutral-300 (#D4D1CC)
 * - Border radius: 6px
 * - Padding: 12px horizontal
 * - Font: text-base (15px)
 * - Focus: 2px ring primary-200 (#BCCCDC), border primary-400 (#829AB1)
 * - Error: Border error (#B91C1C), ring error/20%
 * - Disabled: Background neutral-100 (#F5F4F2), text neutral-400 (#A8A29E)
 *
 * @example
 * // Basic usage
 * <TextField label="Email" placeholder="Enter your email" />
 *
 * @example
 * // With error state
 * <TextField
 *   label="Email"
 *   error
 *   errorMessage="Please enter a valid email address"
 * />
 *
 * @example
 * // With prefix and suffix
 * <TextField
 *   label="Price"
 *   leftElement={<span>$</span>}
 *   rightElement={<span>USD</span>}
 * />
 */
const TextField = React.forwardRef<HTMLInputElement, TextFieldProps>(
  (
    {
      label,
      helperText,
      error = false,
      errorMessage,
      required = false,
      size = 'md',
      leftElement,
      rightElement,
      className = '',
      disabled = false,
      id: providedId,
      'aria-describedby': ariaDescribedBy,
      ...props
    },
    ref
  ) => {
    // Generate stable IDs for accessibility
    const reactId = React.useId();
    const inputId = providedId || `textfield-${reactId}`;
    const helperId = `textfield-helper-${reactId}`;

    // Determine message ID for aria-describedby
    const message = (error && errorMessage) ? errorMessage : helperText;
    const messageId = message ? helperId : undefined;

    // Combine aria-describedby if both provided and generated
    const computedAriaDescribedBy = [ariaDescribedBy, messageId]
      .filter(Boolean)
      .join(' ') || undefined;

    // Build input classes using extracted helper
    const inputClasses = buildInputClasses(size, error, disabled, leftElement, rightElement, className);

    return (
      <div className="w-full">
        {/* Label */}
        {label && (
          <Text
            as="label"
            htmlFor={inputId}
            size="sm"
            weight="medium"
            className="mb-1.5 block text-secondary-700"
          >
            {label}
            {required && (
              <span className="ml-0.5 text-error-700" aria-hidden="true">
                *
              </span>
            )}
          </Text>
        )}

        {/* Input container */}
        <div className="relative">
          {/* Left element */}
          {leftElement && (
            <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3 text-secondary-500">
              {leftElement}
            </div>
          )}

          {/* Input */}
          <input
            ref={ref}
            id={inputId}
            disabled={disabled}
            aria-invalid={error}
            aria-required={required}
            aria-describedby={computedAriaDescribedBy}
            className={inputClasses}
            {...props}
          />

          {/* Right element */}
          {rightElement && (
            <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-3 text-secondary-500">
              {rightElement}
            </div>
          )}
        </div>

        {/* Helper text or error message */}
        <InputMessage error={error} errorMessage={errorMessage} helperText={helperText} helperId={helperId} />
      </div>
    );
  }
);

TextField.displayName = 'TextField';

export { TextField };
